import { IGif } from '@giphy/js-types'

export type EmbedUser = {
    attribution_display_name?: string
    avatar_url?: string
    display_name?: string
    is_public?: boolean
    is_verified?: boolean
    name?: string
    profile_url?: string
    suppress_chrome?: boolean
    username?: string
}

export type ExternalMedia = {
    asset_uid?: string
    provider_name?: string
    start?: number
}

export type CaptionTrack = {
    label: string
    language: string
    url: string
}

type Rendition = {
    height?: number | string
    mp4?: string
    url?: string
    webp?: string
    width?: number | string
}

type EmbedGifExtras = {
    embed_height?: number | string
    embed_width?: number | string
    external_media?: ExternalMedia
    is_anonymous?: boolean
    is_hidden?: boolean
    is_hidden_from_public?: boolean
    is_removed?: boolean
    user?: EmbedUser | null
    video?: {
        assets?: Record<string, { height?: number | string; url?: string; width?: number | string }>
        captions?: Record<string, { srt?: string; vtt?: string }>
        dash_manifest_url?: string
        hls_manifest_url?: string
        native?: string
    }
}

export type EmbedGif = IGif & EmbedGifExtras

export const appendTrackingQuery = (href: string | undefined, queryString?: string) => {
    if (!href || !queryString) {
        return href || '#'
    }

    const [base, hash = ''] = href.split('#')
    const separator = base.includes('?') ? '&' : '?'
    return `${base}${separator}${queryString}${hash ? `#${hash}` : ''}`
}

const numberFrom = (value: number | string | undefined, fallback: number) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const getRendition = (gif: EmbedGif, key: keyof EmbedGif['images']) => gif.images[key] as Rendition | undefined

export const getOriginalDimensions = (gif: EmbedGif) => {
    const original = getRendition(gif, 'original')
    return {
        width: numberFrom(gif.embed_width, numberFrom(original?.width, 480)),
        height: numberFrom(gif.embed_height, numberFrom(original?.height, 360)),
    }
}

export const getGifStillUrl = (gif: EmbedGif) =>
    getRendition(gif, 'fixed_width_still')?.url ||
    getRendition(gif, 'original_still')?.url ||
    getRendition(gif, 'downsized_still')?.url ||
    getRendition(gif, 'original')?.url ||
    ''

export const getGifImageUrl = (gif: EmbedGif) =>
    getRendition(gif, 'fixed_width')?.url ||
    getRendition(gif, 'downsized_large')?.url ||
    getRendition(gif, 'original')?.url ||
    ''

export const getGifWebpUrl = (gif: EmbedGif) =>
    getRendition(gif, 'fixed_width')?.webp || getRendition(gif, 'original')?.webp || ''

export const getGifVideoUrl = (gif: EmbedGif) =>
    gif.is_sticker
        ? ''
        : getRendition(gif, 'original')?.mp4 ||
          getRendition(gif, 'downsized_small')?.mp4 ||
          getRendition(gif, 'fixed_width')?.mp4 ||
          ''

export const getVideoSourceUrl = (gif: EmbedGif) => {
    const assets = gif.video?.assets
    return (
        assets?.['720p']?.url ||
        assets?.['480p']?.url ||
        assets?.['360p']?.url ||
        assets?.source?.url ||
        gif.video?.hls_manifest_url ||
        gif.images.original?.mp4 ||
        ''
    )
}

export const getVideoPosterUrl = (gif: EmbedGif) =>
    gif.video?.previews?.fixed_width?.url ||
    getRendition(gif, 'fixed_width_still')?.url ||
    getRendition(gif, 'original_still')?.url ||
    ''

export const getCaptionTracks = (gif: EmbedGif): CaptionTrack[] =>
    Object.entries(gif.video?.captions || {}).flatMap(([language, urls]) =>
        urls.vtt ? [{ label: language.toUpperCase(), language, url: urls.vtt }] : []
    )

export const getExternalMediaUrl = (externalMedia?: ExternalMedia) => {
    if (!externalMedia?.asset_uid || !externalMedia.provider_name) {
        return ''
    }

    const params = new URLSearchParams()
    if (externalMedia.provider_name === 'youtube') {
        params.set('autoplay', '1')
        params.set('loop', '1')
        params.set('playsinline', '1')
        params.set('fs', '0')
        params.set('rel', '0')
        params.set('volume', '0')
        params.set('enablejsapi', '1')
        params.set('start', String(externalMedia.start ? Math.max(externalMedia.start - 1, 0) : 0))
        return `https://www.youtube.com/embed/${externalMedia.asset_uid}?${params.toString()}`
    }

    if (externalMedia.provider_name === 'vimeo') {
        params.set('autoplay', 'true')
        params.set('byline', 'false')
        params.set('color', '00ccff')
        params.set('loop', 'false')
        params.set('title', 'false')
        return `https://player.vimeo.com/video/${externalMedia.asset_uid}?${params.toString()}`
    }

    return ''
}

export const getEmbedCode = (gif: EmbedGif) => {
    const { width, height } = getOriginalDimensions(gif)
    return `<iframe src="https://giphy.com/embed/${gif.id}" width="${width}" height="${height}" frameBorder="0" class="giphy-embed" allowFullScreen></iframe>`
}

export const getSocialUrls = (gif: EmbedGif) => {
    const url = encodeURIComponent(gif.url)
    const title = encodeURIComponent(gif.title || 'GIPHY')
    const media = encodeURIComponent(getGifImageUrl(gif))

    return {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${title}`,
        twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
    }
}

export const getDisplayName = (user?: EmbedUser | null) =>
    user?.attribution_display_name || user?.display_name || user?.name || user?.username || ''

export const getUserUrl = (user?: EmbedUser | null) =>
    user?.profile_url || (user?.username ? `https://giphy.com/${user.username}` : '')

export const isPublicEmbeddableGif = (gif?: EmbedGif | null) =>
    !!gif &&
    !gif.is_hidden &&
    !gif.is_hidden_from_public &&
    !gif.is_removed &&
    !gif.is_anonymous &&
    gif.rating !== 'nsfw'

export const isEmbeddableVideo = (gif?: EmbedGif | null) =>
    isPublicEmbeddableGif(gif) && !!gif && !!getVideoSourceUrl(gif)
